import { useEffect, useState } from 'react';
import { useTranslations } from 'use-intl';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { userManagementApiClient } from '@/clients/userManagementApiClient';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { InviteCodeEntry } from '@financialharmony/ui/invite-code-entry';
import { userFacingErrorMessage } from '@/utils/userFacingError';

const RESEND_COOLDOWN_SECONDS = 30;

interface InviteCodeFormProps {
  token: string;
  email: string;
  companyName: string;
  onVerified: () => void;
}

export function InviteCodeForm({ token, email, companyName, onVerified }: InviteCodeFormProps) {
  const t = useTranslations('auth');
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const [resendConfirmed, setResendConfirmed] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);

  useEffect(() => {
    if (cooldown === 0) return;
    const timer = setInterval(() => setCooldown((seconds) => Math.max(0, seconds - 1)), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (code.length !== 6) return;
    setVerifyError(null);
    setIsVerifying(true);
    try {
      const { error } = await supabase.auth.verifyOtp({ email, token: code, type: 'invite' });
      if (error) {
        setVerifyError(
          error.code === 'otp_expired'
            ? t('codes.otp_expired')
            : userFacingErrorMessage(error, t('invite.verifyFailed')),
        );
        return;
      }
      onVerified();
    } finally {
      setIsVerifying(false);
    }
  }

  async function handleResend() {
    setIsResending(true);
    setResendError(null);
    setResendConfirmed(false);
    try {
      const result = await userManagementApiClient.resendInvitation(token, window.location.origin);
      if (!result.success) {
        if (result.error === 'already_registered') {
          setAlreadyRegistered(true);
          return;
        }
        setResendError(result.error || t('invite.resendFailed'));
        return;
      }
      setResendConfirmed(true);
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } finally {
      setIsResending(false);
    }
  }

  if (alreadyRegistered) {
    return (
      <div className="space-y-4">
        <Alert>
          <AlertDescription>
            {t('invite.alreadyRegisteredBody')}
          </AlertDescription>
        </Alert>
        <Button
          onClick={() => navigate(`/auth?returnUrl=${encodeURIComponent('/overview')}`)}
          className="w-full"
        >
          {t('invite.logIn')}
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <InviteCodeEntry
        email={email}
        companyName={companyName}
        code={code}
        onCodeChange={setCode}
        components={{ InputOTP, InputOTPGroup, InputOTPSlot }}
      />

      {verifyError && (
        <Alert variant="destructive">
          <AlertDescription>{verifyError}</AlertDescription>
        </Alert>
      )}

      <Button type="submit" disabled={code.length !== 6 || isVerifying} className="w-full">
        {isVerifying ? t('invite.verifying') : t('invite.verify')}
      </Button>

      <div className="flex flex-col items-start gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleResend}
          disabled={isResending || cooldown > 0}
        >
          {cooldown > 0 ? t('invite.resendCooldown', { seconds: String(cooldown) }) : t('invite.resend')}
        </Button>
        {resendConfirmed && (
          <p className="text-xs text-green-600">{t('invite.resendConfirmed')}</p>
        )}
        {resendError && (
          <Alert variant="destructive">
            <AlertDescription>{resendError}</AlertDescription>
          </Alert>
        )}
      </div>
    </form>
  );
}
